import { AxiosError } from 'axios';
import { CountryDetails } from '../../models/shipmentCountryInfo';
import { DeliveryType, Shipment } from '../../models/parcelDeliveryMiles';
import { ErrorResponse } from '../../models/apiResponse';
import {
  FirstMile,
  LastMile,
} from '../../models/delivery-options/delivery-miles';

// Overload signatures
export function getShipmentCountryName(country: CountryDetails | null): string;

// Implementation
export function getShipmentCountryName(country: CountryDetails | null): string {
  if (!country) return '';

  return `${country.emoji} ${country.localized_name}`;
}

const moveCountryToBeginning = (
  arr: CountryDetails[],
  countryCode: string | undefined
): CountryDetails[] => {
  const found = arr.find((obj) => obj.code === countryCode);
  if (found) {
    const filteredArray = arr.filter((obj) => obj.code !== countryCode);
    return [found, ...filteredArray];
  }
  return arr;
};

export function getShipmentCountriesByFilter(
  countries: CountryDetails[],
  filter?: string,
  pinnedCountryCodes?: (string | undefined)[],
  ignoreSorting?: boolean
) {
  let filteredCountries = filter
    ? countries!.filter((i) =>
        i.localized_name
          .toLowerCase()
          .includes(filter.toLocaleLowerCase().trim())
      )
    : countries;

  if (!ignoreSorting) {
    filteredCountries.sort((a, b) =>
      a.localized_name.localeCompare(b.localized_name)
    );
  }

  pinnedCountryCodes
    ?.filter(Boolean)
    .toReversed()
    .forEach((tc) => {
      filteredCountries = moveCountryToBeginning(filteredCountries, tc);
    });

  return filteredCountries;
}

export function getZipCodeDisplayValue(country: CountryDetails | null) {
  const infos = [country?.zipCode, country?.city];
  return infos.filter(Boolean).join(', ');
}

export function getSenderType(shipment: Shipment): DeliveryType {
  return shipment?.sender?.carrier.delivery_service.service_type === 'courier'
    ? 'courier'
    : 'post_box';
}

export function getReceiverType(shipment: Shipment): DeliveryType {
  return shipment?.recipient?.carrier.delivery_service.service_type ===
    'courier'
    ? 'courier'
    : 'post_box';
}

interface DeliveryInfo {
  building?: string;
  street?: string;
  apartment?: string;
  section?: string;
  buzzCode?: string;
  city?: string;
}

export const hasInfoAboutDelivery = (info: DeliveryInfo) =>
  Object.values(info).filter(Boolean).length > 0;

export const getSubjectDetailsSummary = (info: DeliveryInfo) => {
  if (!hasInfoAboutDelivery(info)) return null;
  const { building, street, apartment, section, buzzCode, city } = info;
  const buildingPart = building ? `, ${building}` : '';
  const streetPart = street ? ` ${street}` : '';
  const apartmentPart = apartment ? `, ${apartment}` : '';
  const sectionPart = section ? `, ${section}` : '';
  const buzzCodePart = buzzCode ? `, ${buzzCode}` : '';

  return `${city}${buildingPart}${streetPart}${apartmentPart}${sectionPart}${buzzCodePart}`;
};

export function implementsFirstMile(obj: any): obj is FirstMile {
  return 'middle_miles' in obj;
}

export const getCheapestMile = (miles?: LastMile[], rateSchemaId?: string) => {
  if (!miles || miles.length === 0) return undefined;

  const filtered = rateSchemaId
    ? miles.filter((mile) => mile.rate_schema_id === rateSchemaId)
    : miles;

  return filtered.toSorted((a, b) => a.price - b.price)[0];
};

export const calculatePriceDiff = (
  currentLastMile: LastMile,
  allLastMiles: LastMile[]
) => {
  // current - min
  const cheapestLastMilePrice =
    getCheapestMile(allLastMiles, currentLastMile.rate_schema_id)?.price ?? 0;
  return currentLastMile.price - cheapestLastMilePrice;
};

export const calculateDurationDiff = (
  currentLastMile: LastMile,
  allLastMiles: LastMile[]
) => {
  // current - min
  const cheapestLastMile = getCheapestMile(allLastMiles)!;
  return currentLastMile.duration - cheapestLastMile.duration;
};

export const sortLastMilesByPrice = (miles: LastMile[]) =>
  [...miles].sort((a, b) => a.price - b.price);

export const tryGetErrorMessage = (
  error: unknown,
  statusCode: number,
  errorName: string
) => {
  let errorMessage: string | undefined;
  if (error instanceof AxiosError && error.response?.status === statusCode) {
    errorMessage = (
      error as AxiosError<ErrorResponse, any>
    ).response?.data.errors
      .find((e) => e.key === errorName)
      ?.values?.at(0);
  }

  return errorMessage;
};

export const scrollToFirstInvalidInput = () => {
  const invalidInputs = Array.from(
    document.querySelectorAll('[data-type="invalid-input"]')
  ) as HTMLElement[];

  if (invalidInputs.length === 0) return;
  const firstInvalidInput = invalidInputs.reduce((highest, input) =>
    input.getBoundingClientRect().top < highest.getBoundingClientRect().top
      ? input
      : highest
  );

  firstInvalidInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
  firstInvalidInput.focus();
};
